//src/pages/personnel/PersonnelCalendarPage.jsx
import React, {
  useMemo,
  useState,
} from "react";

import {
  LuCalendarDays,
  LuCalendarRange,
  LuChevronLeft,
  LuChevronRight,
  LuClock3,
  LuRefreshCw,
  LuTruck,
  LuUser,
  LuUsers,
} from "react-icons/lu";

import {
  useTranslation,
} from "react-i18next";

import usePersonnelCalendarData from "../../hooks/usePersonnelCalendarData";
import usePersonnelPermissions from "../../hooks/usePersonnelPermissions";
import PersonnelWorkScheduleModal from "../../components/personnel/PersonnelWorkScheduleModal";

const DAY_MS =
  24 *
  60 *
  60 *
  1000;

const pad2 = (
  value
) =>
  String(
    value
  ).padStart(
    2,
    "0"
  );

const localDateKey = (
  date
) =>
  [
    date.getFullYear(),
    pad2(
      date.getMonth() +
        1
    ),
    pad2(
      date.getDate()
    ),
  ].join(
    "-"
  );

const parseDateOnly = (
  value
) => {
  const [
    year,
    month,
    day,
  ] = String(
    value ||
      ""
  )
    .split("-")
    .map(Number);

  return new Date(
    year,
    month - 1,
    day
  );
};

const startOfDay = (
  date
) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );

const endOfDay = (
  date
) =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999
  );

const overlapsDay = (
  startValue,
  endValue,
  day
) => {
  const start =
    new Date(
      startValue
    );

  const end =
    new Date(
      endValue
    );

  return (
    start <=
      endOfDay(
        day
      ) &&
    end >=
      startOfDay(
        day
      )
  );
};

const getMonthDays = (
  visibleMonth
) => {
  const year =
    visibleMonth.getFullYear();

  const month =
    visibleMonth.getMonth();

  const first =
    new Date(
      year,
      month,
      1
    );

  const mondayOffset =
    (
      first.getDay() +
      6
    ) %
    7;

  const daysInMonth =
    new Date(
      year,
      month +
        1,
      0
    ).getDate();

  const requiredWeeks =
    Math.ceil(
      (
        mondayOffset +
        daysInMonth
      ) /
        7
    );

  const gridStart =
    new Date(
      year,
      month,
      1 -
        mondayOffset
    );

  return Array.from(
    {
      length:
        requiredWeeks *
        7,
    },
    (
      _,
      index
    ) =>
      new Date(
        gridStart.getTime() +
          index *
            DAY_MS
      )
  );
};

const getPersonKey = (
  personType,
  personId
) =>
  `${personType}:${personId}`;

const getWorkDayKey = (
  personType,
  personId,
  workDate
) =>
  `${getPersonKey(
    personType,
    personId
  )}:${workDate}`;

const isSameMonth = (
  date,
  visibleMonth
) =>
  date.getFullYear() ===
    visibleMonth.getFullYear() &&
  date.getMonth() ===
    visibleMonth.getMonth();

const isToday = (
  date
) =>
  localDateKey(
    date
  ) ===
  localDateKey(
    new Date()
  );

const normalizeTime = (
  value
) =>
  value
    ? String(
        value
      ).slice(
        0,
        5
      )
    : "";

const eventClassName = (
  event,
  baseClass
) =>
  [
    baseClass,

    event.kind ===
    "ABSENCE"
      ? "is-absence"
      : "",

    event.kind ===
    "PUBLIC_HOLIDAY"
      ? "is-holiday"
      : "",

    event.kind ===
    "HOLIDAY_WORK"
      ? "is-holiday-work"
      : "",

    event.status ===
    "PENDING"
      ? "is-pending"
      : "",

    event.status ===
    "DRAFT"
      ? "is-draft"
      : "",
  ]
    .filter(Boolean)
    .join(" ");

export default function PersonnelCalendarPage() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "personnel"
    );

  const {
    workDaysManage,
  } =
    usePersonnelPermissions();

  const [
    visibleMonth,
    setVisibleMonth,
  ] =
    useState(
      () => {
        const now =
          new Date();

        return new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        );
      }
    );

  const [
    viewMode,
    setViewMode,
  ] =
    useState(
      "MONTH"
    );

  const [
    selectedDay,
    setSelectedDay,
  ] =
    useState(
      () =>
        startOfDay(
          new Date()
        )
    );

  const [
    typeFilter,
    setTypeFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    personFilter,
    setPersonFilter,
  ] =
    useState(
      "ALL"
    );

  const [
    selectedEvent,
    setSelectedEvent,
  ] =
    useState(
      null
    );

  const [
    workScheduleModal,
    setWorkScheduleModal,
  ] =
    useState(
      null
    );

  const year =
    visibleMonth.getFullYear();

  const {
    people,
    absences,
    publicHolidays,
    holidayWork,
    workDays = [],
    loading,
    refreshing,
    error,
    refresh,
  } =
    usePersonnelCalendarData(
      year
    );

  const locale =
    String(
      i18n.resolvedLanguage ||
        i18n.language ||
        "en"
    );

  const personMap =
    useMemo(
      () => {
        const map =
          new Map();

        people.forEach(
          (
            person
          ) => {
            map.set(
              getPersonKey(
                person.personType,
                person.personId
              ),
              person
            );
          }
        );

        return map;
      },
      [
        people,
      ]
    );

  const workDayMap =
    useMemo(
      () => {
        const map =
          new Map();

        workDays.forEach(
          (
            workDay
          ) => {
            map.set(
              getWorkDayKey(
                workDay.personType,
                workDay.personId,
                workDay.workDate
              ),
              workDay
            );
          }
        );

        return map;
      },
      [
        workDays,
      ]
    );

  const filteredPeople =
    useMemo(
      () => {
        if (
          typeFilter ===
          "ALL"
        ) {
          return people;
        }

        return people.filter(
          (
            person
          ) =>
            person.personType ===
            typeFilter
        );
      },
      [
        people,
        typeFilter,
      ]
    );

  const schedulePeople =
    useMemo(
      () => {
        if (
          personFilter ===
          "ALL"
        ) {
          return filteredPeople;
        }

        const person =
          personMap.get(
            personFilter
          );

        return person
          ? [
              person,
            ]
          : [];
      },
      [
        filteredPeople,
        personFilter,
        personMap,
      ]
    );

  const selectedPerson =
    personFilter !==
    "ALL"
      ? personMap.get(
          personFilter
        ) ||
        null
      : null;

  const monthDays =
    useMemo(
      () =>
        getMonthDays(
          visibleMonth
        ),
      [
        visibleMonth,
      ]
    );

  const eventPassesPersonFilter =
    (
      event
    ) => {
      if (
        !event.personType ||
        !event.personId
      ) {
        return true;
      }

      if (
        typeFilter !==
          "ALL" &&
        event.personType !==
          typeFilter
      ) {
        return false;
      }

      if (
        personFilter !==
        "ALL"
      ) {
        return (
          getPersonKey(
            event.personType,
            event.personId
          ) ===
          personFilter
        );
      }

      return true;
    };

  const calendarEvents =
    useMemo(
      () => {
        const events =
          [];

        publicHolidays.forEach(
          (
            holiday
          ) => {
            events.push({
              id:
                `holiday:${holiday.id}`,

              kind:
                "PUBLIC_HOLIDAY",

              date:
                holiday.date,

              title:
                holiday.name,

              raw:
                holiday,
            });
          }
        );

        absences
          .filter(
            (
              absence
            ) =>
              absence.status ===
                "APPROVED" ||
              absence.status ===
                "PENDING"
          )
          .forEach(
            (
              absence
            ) => {
              const person =
                personMap.get(
                  getPersonKey(
                    absence.personType,
                    absence.personId
                  )
                );

              events.push({
                id:
                  `absence:${absence.id}`,

                kind:
                  "ABSENCE",

                personType:
                  absence.personType,

                personId:
                  absence.personId,

                startAt:
                  absence.startAt,

                endAt:
                  absence.endAt,

                title:
                  person?.displayName ||
                  `${
                    absence.personType ===
                    "DRIVER"
                      ? t(
                          "calendar.driver"
                        )
                      : t(
                          "calendar.user"
                        )
                  } #${absence.personId}`,

                subtitle:
                  t(
                    `absence_types.${absence.absenceType}`,
                    absence.absenceType
                  ),

                status:
                  absence.status,

                raw:
                  absence,
              });
            }
          );

        holidayWork.forEach(
          (
            work
          ) => {
            const person =
              personMap.get(
                getPersonKey(
                  work.personType,
                  work.personId
                )
              );

            events.push({
              id:
                `holiday-work:${work.id}`,

              kind:
                "HOLIDAY_WORK",

              personType:
                work.personType,

              personId:
                work.personId,

              startAt:
                work.startAt,

              endAt:
                work.endAt,

              title:
                person?.displayName ||
                `${
                  work.personType ===
                  "DRIVER"
                    ? t(
                        "calendar.driver"
                      )
                    : t(
                        "calendar.user"
                      )
                } #${work.personId}`,

              subtitle:
                work.publicHoliday?.name ||
                t(
                  "calendar.holiday_work"
                ),

              status:
                work.status,

              raw:
                work,
            });
          }
        );

        return events;
      },
      [
        absences,
        publicHolidays,
        holidayWork,
        personMap,
        t,
      ]
    );

  const visibleEvents =
    useMemo(
      () =>
        calendarEvents.filter(
          eventPassesPersonFilter
        ),
      [
        calendarEvents,
        typeFilter,
        personFilter,
      ]
    );

  const getEventsForDay =
    (
      day
    ) => {
      const key =
        localDateKey(
          day
        );

      return visibleEvents.filter(
        (
          event
        ) => {
          if (
            event.kind ===
            "PUBLIC_HOLIDAY"
          ) {
            return (
              event.date ===
              key
            );
          }

          return overlapsDay(
            event.startAt,
            event.endAt,
            day
          );
        }
      );
    };

  const getWorkDayForPerson =
    (
      person,
      day
    ) =>
      workDayMap.get(
        getWorkDayKey(
          person.personType,
          person.personId,
          localDateKey(
            day
          )
        )
      ) ||
      null;

  const getScheduleSummaryForDay =
    (
      day
    ) => {
      let working =
        0;

      let off =
        0;

      schedulePeople.forEach(
        (
          person
        ) => {
          const workDay =
            getWorkDayForPerson(
              person,
              day
            );

          if (
            workDay?.status ===
            "WORKING"
          ) {
            working +=
              1;
          } else if (
            workDay?.status ===
            "OFF"
          ) {
            off +=
              1;
          }
        }
      );

      return {
        working,
        off,
        unconfigured:
          Math.max(
            0,
            schedulePeople.length -
              working -
              off
          ),
      };
    };

  const getApprovedAbsenceForPersonDay =
    (
      person,
      day
    ) =>
      absences.find(
        (
          absence
        ) =>
          absence.status ===
            "APPROVED" &&
          absence.personType ===
            person.personType &&
          Number(
            absence.personId
          ) ===
            Number(
              person.personId
            ) &&
          overlapsDay(
            absence.startAt,
            absence.endAt,
            day
          )
      ) ||
      null;

  const formatWorkTime =
    (
      workDay
    ) => {
      if (
        !workDay ||
        workDay.status !==
          "WORKING"
      ) {
        return "";
      }

      const start =
        normalizeTime(
          workDay.startTime
        );

      const end =
        normalizeTime(
          workDay.endTime
        );

      if (
        !start ||
        !end
      ) {
        return t(
          "calendar.work_days.no_times",
          {
            defaultValue:
              "Horário não definido",
          }
        );
      }

      return `${start}–${end}`;
    };

  const openWorkDayEditor =
    (
      person,
      day
    ) => {
      if (
        !workDaysManage
      ) {
        return;
      }

      setWorkScheduleModal({
        mode:
          "DAY",

        person,

        date:
          localDateKey(
            day
          ),

        workDay:
          getWorkDayForPerson(
            person,
            day
          ),
      });
    };

  const monthLabel =
    new Intl.DateTimeFormat(
      locale,
      {
        month:
          "long",

        year:
          "numeric",
      }
    ).format(
      visibleMonth
    );

  const dayLabel =
    new Intl.DateTimeFormat(
      locale,
      {
        weekday:
          "long",

        day:
          "2-digit",

        month:
          "long",

        year:
          "numeric",
      }
    ).format(
      selectedDay
    );

  const selectedDayEvents =
    getEventsForDay(
      selectedDay
    );

  const selectedDayScheduleSummary =
    getScheduleSummaryForDay(
      selectedDay
    );

  const weekdayLabels =
    useMemo(
      () => {
        const monday =
          new Date(
            2026,
            7,
            31
          );

        return Array.from(
          {
            length:
              7,
          },
          (
            _,
            index
          ) =>
            new Intl.DateTimeFormat(
              locale,
              {
                weekday:
                  "short",
              }
            ).format(
              new Date(
                monday.getTime() +
                  index *
                    DAY_MS
              )
            )
        );
      },
      [
        locale,
      ]
    );

  const goPreviousMonth =
    () => {
      setVisibleMonth(
        (
          current
        ) =>
          new Date(
            current.getFullYear(),
            current.getMonth() -
              1,
            1
          )
      );
    };

  const goNextMonth =
    () => {
      setVisibleMonth(
        (
          current
        ) =>
          new Date(
            current.getFullYear(),
            current.getMonth() +
              1,
            1
          )
      );
    };

  const goToday =
    () => {
      const now =
        new Date();

      setVisibleMonth(
        new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        )
      );
    };

  const openDay =
    (
      day
    ) => {
      const nextDay =
        startOfDay(
          day
        );

      setSelectedDay(
        nextDay
      );

      setVisibleMonth(
        new Date(
          nextDay.getFullYear(),
          nextDay.getMonth(),
          1
        )
      );

      setViewMode(
        "DAY"
      );
    };

  const goPreviousDay =
    () => {
      setSelectedDay(
        (
          current
        ) => {
          const next =
            new Date(
              current.getFullYear(),
              current.getMonth(),
              current.getDate() -
                1
            );

          setVisibleMonth(
            new Date(
              next.getFullYear(),
              next.getMonth(),
              1
            )
          );

          return next;
        }
      );
    };

  const goNextDay =
    () => {
      setSelectedDay(
        (
          current
        ) => {
          const next =
            new Date(
              current.getFullYear(),
              current.getMonth(),
              current.getDate() +
                1
            );

          setVisibleMonth(
            new Date(
              next.getFullYear(),
              next.getMonth(),
              1
            )
          );

          return next;
        }
      );
    };

  const goTodayInDayView =
    () => {
      const now =
        startOfDay(
          new Date()
        );

      setSelectedDay(
        now
      );

      setVisibleMonth(
        new Date(
          now.getFullYear(),
          now.getMonth(),
          1
        )
      );
    };

  const monthAbsences =
    absences.filter(
      (
        absence
      ) => {
        if (
          absence.status !==
          "APPROVED"
        ) {
          return false;
        }

        return monthDays
          .filter(
            (
              day
            ) =>
              isSameMonth(
                day,
                visibleMonth
              )
          )
          .some(
            (
              day
            ) =>
              overlapsDay(
                absence.startAt,
                absence.endAt,
                day
              )
          );
      }
    ).length;

  const monthHolidays =
    publicHolidays.filter(
      (
        holiday
      ) =>
        isSameMonth(
          parseDateOnly(
            holiday.date
          ),
          visibleMonth
        )
    ).length;

  const monthHolidayWork =
    holidayWork.filter(
      (
        work
      ) =>
        isSameMonth(
          new Date(
            work.startAt
          ),
          visibleMonth
        )
    ).length;

  const dayAbsences =
    absences.filter(
      (
        absence
      ) =>
        absence.status ===
          "APPROVED" &&
        overlapsDay(
          absence.startAt,
          absence.endAt,
          selectedDay
        )
    ).length;

  const dayHolidays =
    publicHolidays.filter(
      (
        holiday
      ) =>
        holiday.date ===
        localDateKey(
          selectedDay
        )
    ).length;

  const dayHolidayWork =
    holidayWork.filter(
      (
        work
      ) =>
        overlapsDay(
          work.startAt,
          work.endAt,
          selectedDay
        )
    ).length;

  const visibleAbsenceCount =
    viewMode ===
    "DAY"
      ? dayAbsences
      : monthAbsences;

  const visibleHolidayCount =
    viewMode ===
    "DAY"
      ? dayHolidays
      : monthHolidays;

  const visibleHolidayWorkCount =
    viewMode ===
    "DAY"
      ? dayHolidayWork
      : monthHolidayWork;

  const capitalizedPeriodLabel =
    (
      viewMode ===
      "DAY"
        ? dayLabel
        : monthLabel
    ).charAt(
      0
    ).toUpperCase() +
    (
      viewMode ===
      "DAY"
        ? dayLabel
        : monthLabel
    ).slice(
      1
    );

  return (
    <section className="personnel-page">
      <div className="personnel-calendar-stats">
        <div className="personnel-stat-card">
          <LuUsers />

          <div>
            <strong>
              {filteredPeople.length}
            </strong>

            <span>
              {t(
                "calendar.stats.people"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-stat-card">
          <LuCalendarDays />

          <div>
            <strong>
              {visibleAbsenceCount}
            </strong>

            <span>
              {t(
                "calendar.stats.absences"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-stat-card">
          <LuCalendarRange />

          <div>
            <strong>
              {visibleHolidayCount}
            </strong>

            <span>
              {t(
                "calendar.stats.holidays"
              )}
            </span>
          </div>
        </div>

        <div className="personnel-stat-card">
          <LuClock3 />

          <div>
            <strong>
              {visibleHolidayWorkCount}
            </strong>

            <span>
              {t(
                "calendar.stats.holiday_work"
              )}
            </span>
          </div>
        </div>
      </div>

      <div className="personnel-calendar-toolbar">
        <div className="personnel-calendar-toolbar-left">
          <div className="personnel-view-toggle">
            <button
              type="button"
              className={
                viewMode ===
                "MONTH"
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "MONTH"
                )
              }
            >
              {t(
                "calendar.views.month",
                {
                  defaultValue:
                    "Mês",
                }
              )}
            </button>

            <button
              type="button"
              className={
                viewMode ===
                "DAY"
                  ? "is-active"
                  : ""
              }
              onClick={() =>
                setViewMode(
                  "DAY"
                )
              }
            >
              {t(
                "calendar.views.day",
                {
                  defaultValue:
                    "Dia",
                }
              )}
            </button>
          </div>

          <div className="personnel-month-controls">
            <button
              type="button"
              onClick={
                viewMode ===
                "DAY"
                  ? goPreviousDay
                  : goPreviousMonth
              }
              aria-label={t(
                "calendar.previous_month"
              )}
            >
              <LuChevronLeft />
            </button>

            <button
              type="button"
              className="personnel-today-btn"
              onClick={
                viewMode ===
                "DAY"
                  ? goTodayInDayView
                  : goToday
              }
            >
              {t(
                "calendar.today"
              )}
            </button>

            <button
              type="button"
              onClick={
                viewMode ===
                "DAY"
                  ? goNextDay
                  : goNextMonth
              }
              aria-label={t(
                "calendar.next_month"
              )}
            >
              <LuChevronRight />
            </button>

            <strong className="personnel-month-label">
              {capitalizedPeriodLabel}
            </strong>
          </div>
        </div>

        <div className="personnel-calendar-filters">
          <select
            value={
              typeFilter
            }
            onChange={(
              event
            ) => {
              setTypeFilter(
                event.target.value
              );

              setPersonFilter(
                "ALL"
              );
            }}
          >
            <option value="ALL">
              {t(
                "calendar.filters.everyone"
              )}
            </option>

            <option value="USER">
              {t(
                "calendar.filters.users"
              )}
            </option>

            <option value="DRIVER">
              {t(
                "calendar.filters.drivers"
              )}
            </option>
          </select>

          <select
            value={
              personFilter
            }
            onChange={(
              event
            ) =>
              setPersonFilter(
                event.target.value
              )
            }
          >
            <option value="ALL">
              {t(
                "calendar.filters.all_people"
              )}
            </option>

            {filteredPeople.map(
              (
                person
              ) => (
                <option
                  key={getPersonKey(
                    person.personType,
                    person.personId
                  )}
                  value={getPersonKey(
                    person.personType,
                    person.personId
                  )}
                >
                  {person.displayName}
                  {" · "}
                  {person.personType ===
                  "DRIVER"
                    ? t(
                        "calendar.driver"
                      )
                    : t(
                        "calendar.user"
                      )}
                </option>
              )
            )}
          </select>

          {workDaysManage && (
            <button
              type="button"
              className="personnel-work-days-manage-btn"
              onClick={() =>
                setWorkScheduleModal({
                  mode:
                    "BULK",
                })
              }
            >
              <LuCalendarDays />

              <span>
                {t(
                  "calendar.work_days.manage",
                  {
                    defaultValue:
                      "Gerir dias de trabalho",
                  }
                )}
              </span>
            </button>
          )}

          <button
            type="button"
            className="personnel-refresh-btn"
            onClick={() =>
              refresh({
                silent:
                  true,
              })
            }
            disabled={
              refreshing
            }
          >
            <LuRefreshCw
              className={
                refreshing
                  ? "is-spinning"
                  : ""
              }
            />

            <span>
              {t(
                "calendar.refresh"
              )}
            </span>
          </button>
        </div>
      </div>

      {error && (
        <div className="personnel-calendar-warning">
          {t(
            "calendar.refresh_warning"
          )}
        </div>
      )}

      {viewMode ===
      "MONTH" ? (
        <div className="personnel-calendar-card">
          <div className="personnel-weekdays">
            {weekdayLabels.map(
              (
                label
              ) => (
                <div
                  key={
                    label
                  }
                >
                  {label}
                </div>
              )
            )}
          </div>

          <div className="personnel-month-grid">
            {monthDays.map(
              (
                day
              ) => {
                const events =
                  getEventsForDay(
                    day
                  );

                const shown =
                  events.slice(
                    0,
                    3
                  );

                const remaining =
                  Math.max(
                    0,
                    events.length -
                      shown.length
                  );

                const hiddenEvents =
                  events.slice(
                    shown.length
                  );

                const hiddenEventsTitle =
                  hiddenEvents
                    .map(
                      (
                        event
                      ) =>
                        [
                          event.title,
                          event.subtitle,
                        ]
                          .filter(Boolean)
                          .join(
                            " — "
                          )
                    )
                    .join(
                      "\n"
                    );

                const scheduleSummary =
                  getScheduleSummaryForDay(
                    day
                  );

                const singleWorkDay =
                  selectedPerson
                    ? getWorkDayForPerson(
                        selectedPerson,
                        day
                      )
                    : null;

                const showSchedule =
                  isSameMonth(
                    day,
                    visibleMonth
                  ) &&
                  schedulePeople.length >
                    0;

                return (
                  <div
                    key={localDateKey(
                      day
                    )}
                    className={[
                      "personnel-day",

                      !isSameMonth(
                        day,
                        visibleMonth
                      )
                        ? "is-outside"
                        : "",

                      isToday(
                        day
                      )
                        ? "is-today"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <button
                      type="button"
                      className="personnel-day-number"
                      onClick={() =>
                        openDay(
                          day
                        )
                      }
                      title={t(
                        "calendar.open_day",
                        {
                          defaultValue:
                            "Abrir vista diária",
                        }
                      )}
                    >
                      {day.getDate()}
                    </button>

                    {showSchedule && (
                      personFilter !==
                      "ALL" ? (
                        <button
                          type="button"
                          className={[
                            "personnel-work-day-pill",

                            singleWorkDay?.status ===
                            "WORKING"
                              ? "is-working"
                              : "",

                            singleWorkDay?.status ===
                            "OFF"
                              ? "is-off"
                              : "",

                            !singleWorkDay
                              ? "is-unconfigured"
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                          onClick={() =>
                            workDaysManage
                              ? openWorkDayEditor(
                                  selectedPerson,
                                  day
                                )
                              : openDay(
                                  day
                                )
                          }
                        >
                          <span className="personnel-work-day-pill-dot" />

                          <span>
                            {singleWorkDay?.status ===
                            "WORKING"
                              ? t(
                                  "calendar.work_days.working",
                                  {
                                    defaultValue:
                                      "Trabalha",
                                  }
                                )
                              : singleWorkDay?.status ===
                                "OFF"
                              ? t(
                                  "calendar.work_days.off",
                                  {
                                    defaultValue:
                                      "Folga",
                                  }
                                )
                              : t(
                                  "calendar.work_days.unconfigured",
                                  {
                                    defaultValue:
                                      "Não configurado",
                                  }
                                )}

                            {singleWorkDay?.status ===
                              "WORKING" &&
                              singleWorkDay.startTime &&
                              singleWorkDay.endTime && (
                                <small>
                                  {normalizeTime(
                                    singleWorkDay.startTime
                                  )}
                                  –
                                  {normalizeTime(
                                    singleWorkDay.endTime
                                  )}
                                </small>
                              )}
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="personnel-work-day-summary"
                          onClick={() =>
                            openDay(
                              day
                            )
                          }
                        >
                          <span className="is-working">
                            {t(
                              "calendar.work_days.summary_working",
                              {
                                defaultValue:
                                  "{{count}} trabalham",

                                count:
                                  scheduleSummary.working,
                              }
                            )}
                          </span>

                          <span className="is-off">
                            {t(
                              "calendar.work_days.summary_off",
                              {
                                defaultValue:
                                  "{{count}} folga",

                                count:
                                  scheduleSummary.off,
                              }
                            )}
                          </span>

                          {scheduleSummary.unconfigured >
                            0 && (
                            <span className="is-unconfigured">
                              {t(
                                "calendar.work_days.summary_unconfigured",
                                {
                                  defaultValue:
                                    "{{count}} sem plano",

                                  count:
                                    scheduleSummary.unconfigured,
                                }
                              )}
                            </span>
                          )}
                        </button>
                      )
                    )}

                    <div className="personnel-day-events">
                      {shown.map(
                        (
                          event
                        ) => (
                          <button
                            type="button"
                            key={
                              event.id
                            }
                            className={eventClassName(
                              event,
                              "personnel-calendar-event"
                            )}
                            onClick={() =>
                              setSelectedEvent(
                                event
                              )
                            }
                          >
                            {event.kind ===
                              "ABSENCE" &&
                              (
                                event.personType ===
                                "DRIVER"
                                  ? <LuTruck />
                                  : <LuUser />
                              )}

                            {event.kind ===
                              "PUBLIC_HOLIDAY" && (
                              <LuCalendarRange />
                            )}

                            {event.kind ===
                              "HOLIDAY_WORK" && (
                              <LuClock3 />
                            )}

                            <span>
                              {event.title}
                            </span>
                          </button>
                        )
                      )}

                      {remaining >
                        0 && (
                        <button
                          type="button"
                          className="personnel-more-events"
                          title={
                            hiddenEventsTitle
                          }
                          onClick={() =>
                            openDay(
                              day
                            )
                          }
                        >
                          +{remaining}{" "}
                          {t(
                            "calendar.more"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
            )}
          </div>

          {loading && (
            <div className="personnel-calendar-loading">
              {t(
                "calendar.loading"
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="personnel-day-view">
          <div className="personnel-day-view-header">
            <div>
              <span>
                {t(
                  "calendar.day_view.kicker",
                  {
                    defaultValue:
                      "Agenda diária",
                  }
                )}
              </span>

              <strong>
                {capitalizedPeriodLabel}
              </strong>
            </div>

            <span className="personnel-day-view-count">
              {selectedDayEvents.length}{" "}
              {t(
                "calendar.day_view.events",
                {
                  defaultValue:
                    "eventos",
                }
              )}
            </span>
          </div>

          <div className="personnel-day-roster">
            <div className="personnel-day-roster-header">
              <div>
                <strong>
                  {t(
                    "calendar.work_days.day_roster",
                    {
                      defaultValue:
                        "Planeamento do dia",
                    }
                  )}
                </strong>

                <span>
                  {t(
                    "calendar.work_days.day_roster_help",
                    {
                      defaultValue:
                        "Trabalho e folgas planeados para as pessoas atualmente filtradas.",
                    }
                  )}
                </span>
              </div>

              <div className="personnel-day-roster-summary">
                <span className="is-working">
                  {selectedDayScheduleSummary.working}{" "}
                  {t(
                    "calendar.work_days.working",
                    {
                      defaultValue:
                        "Trabalha",
                    }
                  )}
                </span>

                <span className="is-off">
                  {selectedDayScheduleSummary.off}{" "}
                  {t(
                    "calendar.work_days.off",
                    {
                      defaultValue:
                        "Folga",
                    }
                  )}
                </span>

                <span className="is-unconfigured">
                  {selectedDayScheduleSummary.unconfigured}{" "}
                  {t(
                    "calendar.work_days.unconfigured",
                    {
                      defaultValue:
                        "Não configurado",
                    }
                  )}
                </span>
              </div>
            </div>

            {schedulePeople.length ===
            0 ? (
              <div className="personnel-day-roster-empty">
                {t(
                  "calendar.work_days.no_people_for_filter",
                  {
                    defaultValue:
                      "Não existem pessoas para o filtro selecionado.",
                  }
                )}
              </div>
            ) : (
              <div className="personnel-day-roster-list">
                {schedulePeople.map(
                  (
                    person
                  ) => {
                    const workDay =
                      getWorkDayForPerson(
                        person,
                        selectedDay
                      );

                    const approvedAbsence =
                      getApprovedAbsenceForPersonDay(
                        person,
                        selectedDay
                      );

                    return (
                      <div
                        className="personnel-day-roster-row"
                        key={getPersonKey(
                          person.personType,
                          person.personId
                        )}
                      >
                        <div className="personnel-day-roster-person-icon">
                          {person.personType ===
                          "DRIVER"
                            ? <LuTruck />
                            : <LuUser />}
                        </div>

                        <div className="personnel-day-roster-person">
                          <strong>
                            {person.displayName}
                          </strong>

                          <span>
                            {person.personType ===
                            "DRIVER"
                              ? t(
                                  "calendar.driver"
                                )
                              : t(
                                  "calendar.user"
                                )}
                          </span>
                        </div>

                        <div className="personnel-day-roster-planning">
                          <span
                            className={[
                              "personnel-day-roster-status",

                              workDay?.status ===
                              "WORKING"
                                ? "is-working"
                                : "",

                              workDay?.status ===
                              "OFF"
                                ? "is-off"
                                : "",

                              !workDay
                                ? "is-unconfigured"
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            {workDay?.status ===
                            "WORKING"
                              ? t(
                                  "calendar.work_days.working",
                                  {
                                    defaultValue:
                                      "Trabalha",
                                  }
                                )
                              : workDay?.status ===
                                "OFF"
                              ? t(
                                  "calendar.work_days.off",
                                  {
                                    defaultValue:
                                      "Folga",
                                  }
                                )
                              : t(
                                  "calendar.work_days.unconfigured",
                                  {
                                    defaultValue:
                                      "Não configurado",
                                  }
                                )}
                          </span>

                          {workDay?.status ===
                            "WORKING" && (
                            <small>
                              {formatWorkTime(
                                workDay
                              )}

                              {Number(
                                workDay.breakMinutes ||
                                  0
                              ) >
                                0 && (
                                <>
                                  {" · "}
                                  {workDay.breakMinutes}
                                  {t(
                                    "units.minute_short"
                                  )}{" "}
                                  {t(
                                    "calendar.work_days.break_short",
                                    {
                                      defaultValue:
                                        "pausa",
                                    }
                                  )}
                                </>
                              )}
                            </small>
                          )}
                        </div>

                        <div className="personnel-day-roster-overrides">
                          {approvedAbsence && (
                            <span className="is-absence">
                              {t(
                                `absence_types.${approvedAbsence.absenceType}`,
                                approvedAbsence.absenceType
                              )}
                            </span>
                          )}
                        </div>

                        {workDaysManage && (
                          <button
                            type="button"
                            className="personnel-day-roster-edit"
                            onClick={() =>
                              openWorkDayEditor(
                                person,
                                selectedDay
                              )
                            }
                          >
                            {t(
                              "calendar.work_days.edit",
                              {
                                defaultValue:
                                  "Editar",
                              }
                            )}
                          </button>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </div>

          {selectedDayEvents.length ===
          0 ? (
            <div className="personnel-day-view-empty">
              <LuCalendarDays />

              <strong>
                {t(
                  "calendar.day_view.empty",
                  {
                    defaultValue:
                      "Sem outros eventos neste dia",
                  }
                )}
              </strong>

              <span>
                {t(
                  "calendar.day_view.empty_subtitle",
                  {
                    defaultValue:
                      "Não existem ausências, feriados ou trabalho em feriados para esta data.",
                  }
                )}
              </span>
            </div>
          ) : (
            <div className="personnel-day-view-list">
              {selectedDayEvents.map(
                (
                  event
                ) => (
                  <button
                    type="button"
                    className={eventClassName(
                      event,
                      "personnel-day-view-event"
                    )}
                    key={
                      event.id
                    }
                    onClick={() =>
                      setSelectedEvent(
                        event
                      )
                    }
                  >
                    <span className="personnel-day-view-event-icon">
                      {event.kind ===
                        "ABSENCE" &&
                        (
                          event.personType ===
                          "DRIVER"
                            ? <LuTruck />
                            : <LuUser />
                        )}

                      {event.kind ===
                        "PUBLIC_HOLIDAY" && (
                        <LuCalendarRange />
                      )}

                      {event.kind ===
                        "HOLIDAY_WORK" && (
                        <LuClock3 />
                      )}
                    </span>

                    <div className="personnel-day-view-event-copy">
                      <strong>
                        {event.title}
                      </strong>

                      <span>
                        {event.subtitle ||
                          (
                            event.kind ===
                            "PUBLIC_HOLIDAY"
                              ? t(
                                  "calendar.details.public_holiday"
                                )
                              : "—"
                          )}
                      </span>
                    </div>

                    {event.status && (
                      <span className="personnel-day-view-status">
                        {t(
                          `statuses.${event.status}`,
                          event.status
                        )}
                      </span>
                    )}
                  </button>
                )
              )}
            </div>
          )}

          {loading && (
            <div className="personnel-calendar-loading">
              {t(
                "calendar.loading"
              )}
            </div>
          )}
        </div>
      )}

      <div className="personnel-calendar-legend">
        <span>
          <i className="legend-dot legend-dot--working" />
          {t(
            "calendar.legend.working",
            {
              defaultValue:
                "Trabalha",
            }
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--off" />
          {t(
            "calendar.legend.off",
            {
              defaultValue:
                "Folga",
            }
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--unconfigured" />
          {t(
            "calendar.legend.unconfigured",
            {
              defaultValue:
                "Não configurado",
            }
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--absence" />
          {t(
            "calendar.legend.absence"
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--holiday" />
          {t(
            "calendar.legend.holiday"
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--work" />
          {t(
            "calendar.legend.holiday_work"
          )}
        </span>

        <span>
          <i className="legend-dot legend-dot--pending" />
          {t(
            "calendar.legend.pending"
          )}
        </span>
      </div>

      {selectedEvent && (
        <div
          className="personnel-event-overlay"
          onMouseDown={(
            event
          ) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              setSelectedEvent(
                null
              );
            }
          }}
        >
          <div className="personnel-event-modal">
            <div className="personnel-event-modal-header">
              <div>
                <span className="personnel-event-kicker">
                  {selectedEvent.kind ===
                  "ABSENCE"
                    ? t(
                        "calendar.details.absence"
                      )
                    : selectedEvent.kind ===
                      "PUBLIC_HOLIDAY"
                    ? t(
                        "calendar.details.public_holiday"
                      )
                    : t(
                        "calendar.details.holiday_work"
                      )}
                </span>

                <h3>
                  {selectedEvent.title}
                </h3>
              </div>

              <button
                type="button"
                onClick={() =>
                  setSelectedEvent(
                    null
                  )
                }
                aria-label={t(
                  "common.close"
                )}
              >
                ×
              </button>
            </div>

            {selectedEvent.subtitle && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.type"
                  )}
                </span>

                <strong>
                  {selectedEvent.subtitle}
                </strong>
              </div>
            )}

            {selectedEvent.status && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.status"
                  )}
                </span>

                <strong>
                  {t(
                    `statuses.${selectedEvent.status}`,
                    selectedEvent.status
                  )}
                </strong>
              </div>
            )}

            {selectedEvent.raw?.startAt && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.start"
                  )}
                </span>

                <strong>
                  {new Intl.DateTimeFormat(
                    locale,
                    {
                      dateStyle:
                        "medium",

                      timeStyle:
                        "short",
                    }
                  ).format(
                    new Date(
                      selectedEvent.raw.startAt
                    )
                  )}
                </strong>
              </div>
            )}

            {selectedEvent.raw?.endAt && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.end"
                  )}
                </span>

                <strong>
                  {new Intl.DateTimeFormat(
                    locale,
                    {
                      dateStyle:
                        "medium",

                      timeStyle:
                        "short",
                    }
                  ).format(
                    new Date(
                      selectedEvent.raw.endAt
                    )
                  )}
                </strong>
              </div>
            )}

            {selectedEvent.kind ===
              "PUBLIC_HOLIDAY" && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.date"
                  )}
                </span>

                <strong>
                  {new Intl.DateTimeFormat(
                    locale,
                    {
                      dateStyle:
                        "long",
                    }
                  ).format(
                    parseDateOnly(
                      selectedEvent.raw.date
                    )
                  )}
                </strong>
              </div>
            )}

            {selectedEvent.raw?.reason && (
              <div className="personnel-event-detail-row">
                <span>
                  {t(
                    "calendar.details.reason"
                  )}
                </span>

                <strong>
                  {selectedEvent.raw.reason}
                </strong>
              </div>
            )}

            {selectedEvent.kind ===
              "HOLIDAY_WORK" && (
              <>
                <div className="personnel-event-detail-row">
                  <span>
                    {t(
                      "calendar.details.worked_time"
                    )}
                  </span>

                  <strong>
                    {Math.floor(
                      Number(
                        selectedEvent.raw.workedMinutes ||
                          0
                      ) /
                        60
                    )}
                    {t(
                      "units.hour_short"
                    )}{" "}
                    {Number(
                      selectedEvent.raw.workedMinutes ||
                        0
                    ) %
                      60}
                    {t(
                      "units.minute_short"
                    )}
                  </strong>
                </div>

                <div className="personnel-event-detail-row">
                  <span>
                    {t(
                      "calendar.details.compensation"
                    )}
                  </span>

                  <strong>
                    {t(
                      `compensation_types.${selectedEvent.raw.compensationType}`,
                      selectedEvent.raw.compensationType
                    )}
                  </strong>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {workScheduleModal && (
        <PersonnelWorkScheduleModal
          mode={
            workScheduleModal.mode
          }
          people={
            filteredPeople
          }
          visibleMonth={
            visibleMonth
          }
          initialPersonKeys={
            selectedPerson
              ? [
                  getPersonKey(
                    selectedPerson.personType,
                    selectedPerson.personId
                  ),
                ]
              : []
          }
          person={
            workScheduleModal.person ||
            null
          }
          date={
            workScheduleModal.date ||
            null
          }
          workDay={
            workScheduleModal.workDay ||
            null
          }
          onClose={() =>
            setWorkScheduleModal(
              null
            )
          }
          onChanged={() =>
            refresh({
              silent:
                true,
            })
          }
        />
      )}
    </section>
  );
}
